import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

// Animation variants for staggered card appearance
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

// YouTube video data
const youtubeVideos = [
  { src: "https://www.youtube.com/embed/w-3vgeqtjiA" },
  { src: "https://www.youtube.com/embed/OEXyITvJupo" },
  { src: "https://www.youtube.com/embed/11pLCCEeG4U" },
  { src: "https://www.youtube.com/embed/cW-Wpzr5Qfo" },
  { src: "https://www.youtube.com/embed/8VjpX9EBWOc" },
  { src: "https://www.youtube.com/embed/Ghd5LBoLS1Y" },
];

// Local tutorial videos
const localVideos = [
  { 
    src: `/Overview Tab.mp4`,
    title: "Dashboard Overview",
    description: "Learn how to navigate the dashboard"
  },
  {
    src: `/Settings Tab.mp4`,
    title: "Settings Management",
    description: "How to manage your settings"
  },
  {
    src: `/Songs Tab.mp4`,
    title: "Songs Management",
    description: "How to manage songs in the system"
  }
];

// Helper component for video with error handling
const VideoPlayer = ({ src, title }) => {
  const [error, setError] = React.useState(false);
  const videoRef = React.useRef(null);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleError = () => {
      console.error('Video error:', video.error);
      setError(true);
    };

    video.addEventListener('error', handleError);
    return () => video.removeEventListener('error', handleError);
  }, [src]);

  if (error) {
    return (
      <div className="w-full aspect-video bg-gray-100 rounded-lg flex items-center justify-center text-gray-600 text-center p-4">
        <div>
          <p>Could not load video: {title}</p>
          <p className="text-xs mt-2">Please check if the file exists at: {src}</p>
        </div>
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      controls
      preload="metadata"
      className="w-full aspect-video bg-black rounded-lg"
    >
      <source src={src} type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  );
};

const Tutorial = () => {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const translatedVideos = t("tutorial.videos", { returnObjects: true }) || [];

  // Check if user is admin on component mount
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    const role = localStorage.getItem("adminRole");
    const isAdmin = token && ["admin", "super user"].includes(role);
    setIsAuthenticated(!!isAdmin);
  }, []);

  return (
    <div className="flex flex-col overflow-hidden h-full mx-auto px-4 py-4 sm:px-6 sm:py-6">
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 text-center text-gray-800">
        {t("tutorial.title")}
      </h2>
      <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto text-sm sm:text-base lg:text-lg">
        {t("tutorial.sectionTitle")}
      </p>
      
      {isAuthenticated && (
        <div className="mb-10">
          <h3 className="text-xl font-semibold mb-4 text-gray-800">Admin Tutorials</h3>
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
          >
            <AnimatePresence>
              {localVideos.map((video, index) => (
                <motion.div
                  key={`local-${index}`}
                  variants={item}
                  whileHover={{ scale: 1.02 }}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  <VideoPlayer src={video.src} title={video.title} />
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-800 line-clamp-2 text-sm sm:text-base h-12 sm:h-14 min-h-12">
                      {video.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600">
                      {video.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      )}

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
      >
        <AnimatePresence>
          {youtubeVideos.map((video, index) => {
            const videoData = translatedVideos[index] || {};
            return (
              <motion.div
                key={`youtube-${index}`}
                variants={item}
                whileHover={{ scale: 1.02 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                <iframe
                  src={video.src}
                  title={videoData.title || `Video ${index + 1}`}
                  className="w-full aspect-video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
                <div className="p-4">
                  <h3 className="font-semibold text-gray-800 line-clamp-2 text-sm sm:text-base h-12 sm:h-14 min-h-12">
                    {videoData.title || `Video ${index + 1}`}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {videoData.description || t("tutorial.defaultDescription")}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default Tutorial;
